import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';

import { ApiEndpoint } from '@shared/shared/enums/api-endpoints.enum';
import { IMemberEventBooking } from '@shared/shared/interfaces/member-event.interfaces';
import * as ObjectLib from '@shared/utils/object.lib';
import { replaceTemplateKeys } from '@shared/utils/url.lib';
import * as MemberEventBookingActions from '../actions/member-event-booking.actions';
import * as fromReducers from '../reducers';

@Injectable()
export class MemberEventBookingEffects {
  constructor(
    private actions$: Actions,
    private http: HttpClient,
    private ngrxStore: Store<{ 'member-event': fromReducers.MemberEventState }>
  ) {}

  public getBooking$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MemberEventBookingActions.GET_BOOKING),
      switchMap((action: MemberEventBookingActions.GetBooking) => {
        const url = `${ApiEndpoint.MEMBER_EVENTS_BOOKINGS}${action.payload.id}/`;

        return this.http.get<IMemberEventBooking>(url).pipe(
          map((data) => new MemberEventBookingActions.GetBookingSuccess(data)),
          catchError((err) => of(new MemberEventBookingActions.GetBookingFailure({ value: err.error })))
        );
      })
    )
  );

  public createBooking$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MemberEventBookingActions.CREATE_BOOKING),
      withLatestFrom(this.ngrxStore),
      switchMap(([, state$]) => {
        const { event_date_url: event_date, tickets, recaptcha } = state$['member-event'].memberEventBooking;
        const data = {
          event_date,
          tickets,
          'g-recaptcha-response': recaptcha,
        };

        return this.http.post<IMemberEventBooking>(ApiEndpoint.MEMBER_EVENTS_BOOKINGS, data).pipe(
          map((booking) => new MemberEventBookingActions.CreateBookingSuccess(booking)),
          catchError((err) => of(new MemberEventBookingActions.CreateBookingFailure({ value: err.error })))
        );
      })
    )
  );

  public updateBooking$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MemberEventBookingActions.UPDATE_BOOKING),
      switchMap((action: MemberEventBookingActions.UpdateBooking) => {
        const url = replaceTemplateKeys(ApiEndpoint.MEMBER_EVENTS_BOOKINGS_ORDER, { id: action.payload.id });

        return this.http.patch<{ data: Record<string, any> }>(url, action.payload.data).pipe(
          map((data) => new MemberEventBookingActions.UpdateBookingSuccess(data)),
          catchError(() =>
            of(
              new MemberEventBookingActions.UpdateBookingFailure({
                value: 'Could not complete member event booking',
              })
            )
          )
        );
      })
    )
  );

  public updateBookingLocal$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MemberEventBookingActions.UPDATE_BOOKING_LOCAL),
      withLatestFrom(this.ngrxStore),
      switchMap(([action, state$]: [MemberEventBookingActions.UpdateBookingLocal, any]) =>
        of(this.updateBookingTickets(action, state$))
      ),
      map((booking) => new MemberEventBookingActions.UpdateBookingLocalSuccess(booking)),
      catchError((err) => of(new MemberEventBookingActions.UpdateBookingLocalFailure({ value: err.error })))
    )
  );

  public clearBookingLocal$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MemberEventBookingActions.CLEAR_BOOKING_LOCAL),
      map(() => new MemberEventBookingActions.ClearBookingLocalSuccess()),
      catchError(() =>
        of(
          new MemberEventBookingActions.ClearBookingLocalFailure({
            value: 'Could not clear member event booking',
          })
        )
      )
    )
  );

  private updateBookingTickets(
    action: MemberEventBookingActions.UpdateBookingLocal,
    state$: { 'member-event': fromReducers.MemberEventState }
  ): IMemberEventBooking {
    const state = state$['member-event'].memberEventBooking;
    const _data = ObjectLib.extractAllExceptByKeys(action.payload.data, ['ticket']);

    if (!Object.prototype.hasOwnProperty.call(action.payload.data, 'ticket')) {
      return {
        ...state,
        ..._data,
      };
    }
    const tickets = [...state.tickets, ...action.payload.data.ticket];

    return {
      ...state,
      ..._data,
      tickets,
    };
  }
}
